/* Endpoint hooks: client + active filters + useQuery, one per data need.
 * Each returns the full {data, meta} envelope (components want meta for the
 * freshness / data-availability notes). Pass an explicit query to override the
 * global filters (the session detail page scopes to a single session). */

import { useAnalyticsClient } from "./client";
import { useAgentScope } from "./agentScope";
import { toApiError } from "./errors";
import { queryKey, useFilters } from "./filters";
import { useQuery, type QueryState } from "./useQuery";
import { logger } from "../services/telemetry";
import type {
  AgentsResponse,
  AnalyticsQuery,
  ErrorBreakdownResponse,
  ErrorsResponse,
  ModelsResponse,
  SessionDetailResponse,
  SessionsResponse,
  SortKey,
  SortOrder,
  SummaryResponse,
  TransformersResponse,
  TrendBucket,
  TrendsResponse,
} from "./types";

function useScopedQuery(override?: AnalyticsQuery): AnalyticsQuery {
  const { query } = useFilters();
  const agentId = useAgentScope();
  if (override) return override;

  // Inside an agent workspace, pin every read to that agent. Elsewhere the
  // query is unchanged (home rollup + the cross-agent aggregate stay org-wide).
  return agentId ? { ...query, agent_id: [agentId] } : query;
}

export function useAgentRollups(
  override?: AnalyticsQuery,
): QueryState<AgentsResponse> {
  const client = useAnalyticsClient();
  const { query } = useFilters();
  const q = override ?? query;
  return useQuery(
    () => client.getAgents(q),
    queryKey(q, "agents"),
    "loadAgents",
  );
}

/** Per-model rollup for the "Models" section. Reads the raw filter query rather
 *  than the agent-scoped one (like useAgentRollups), so an agent detail page
 *  mounting it would not silently narrow to that agent. The detail page's own
 *  breakdown comes from AgentRollup.models instead — already fetched, so it costs
 *  no extra request. */
export function useModelRollups(
  override?: AnalyticsQuery,
): QueryState<ModelsResponse> {
  const client = useAnalyticsClient();
  const { query } = useFilters();
  const q = override ?? query;
  return useQuery(
    () => client.getModels(q),
    queryKey(q, "models"),
    "loadModels",
  );
}

export function useSummary(
  override?: AnalyticsQuery,
  /** Optional cache-key salt. Appended to the key only (never the fetched
   *  query), so a caller can force its OWN summary read to refetch without
   *  bumping the global refresh generation (which re-keys every hook). Used by
   *  the onboarding traffic poll to stay isolated to its own subscriber. */
  keySalt?: string,
): QueryState<SummaryResponse> {
  const client = useAnalyticsClient();
  const query = useScopedQuery(override);
  return useQuery(
    () => client.getSummary(query),
    queryKey(query, keySalt ? `summary|${keySalt}` : "summary"),
    "loadSummary",
  );
}

export function useTrends(
  bucket?: TrendBucket,
  override?: AnalyticsQuery,
): QueryState<TrendsResponse> {
  const client = useAnalyticsClient();
  const query = useScopedQuery(override);
  return useQuery(
    () => client.getTrends({ ...query, bucket }),
    queryKey(query, `trends-${bucket ?? "auto"}`),
    "loadTrends",
  );
}

export function useTransformers(
  override?: AnalyticsQuery,
): QueryState<TransformersResponse> {
  const client = useAnalyticsClient();
  const query = useScopedQuery(override);
  return useQuery(
    () => client.getTransformers(query),
    queryKey(query, "transformers"),
    "loadTransformers",
  );
}

export interface SessionListParams {
  sort?: SortKey;
  order?: SortOrder;
  limit?: number;
  cursor?: string;
}

export function useSessions(
  params: SessionListParams = {},
  override?: AnalyticsQuery,
): QueryState<SessionsResponse> {
  const client = useAnalyticsClient();
  const query = useScopedQuery(override);
  return useQuery(
    () => client.getSessions({ ...query, ...params }),
    queryKey(query, `sessions-${JSON.stringify(params)}`),
    "loadSessions",
  );
}

export interface ErrorListParams {
  limit?: number;
  cursor?: string;
  /** HTTP statuses to keep — the failure drill-down's status facet (OPSS-352). */
  status?: number[];
  /** Served providers to keep (OPSS-352). */
  provider?: string[];
}

export function useErrors(
  params: ErrorListParams = {},
  override?: AnalyticsQuery,
): QueryState<ErrorsResponse> {
  const client = useAnalyticsClient();
  const query = useScopedQuery(override);
  return useQuery(
    () => client.getErrors({ ...query, ...params }),
    queryKey(query, `errors-${JSON.stringify(params)}`),
    "loadErrors",
  );
}

/* `/errors/breakdown` lands with the BFF half of OPSS-352, so until that
 * deploys the endpoint 404s — an expected absence, not an incident. `useQuery`
 * caches successes only, so left alone that 404 re-fires on every drawer open
 * and every remount, and each one reports itself as a hard API failure
 * (`api request failed` in Loki + Amplitude ApiRequestFailed +
 * http_client_errors_total), indistinguishable from a real outage in the funnel
 * an on-call greps. So: latch the first one process-wide and probe once per
 * session. */
let breakdownUnavailable = false;

/** Clear the once-per-session `/errors/breakdown` probe latch. Tests only —
 *  the latch is deliberately process-wide, so a suite that exercises the 404
 *  path would otherwise disable the endpoint for every case after it. */
export function resetErrorBreakdownProbe(): void {
  breakdownUnavailable = false;
}

/** Server-side aggregate of the same failures `useErrors` lists. Takes the same
 *  params (minus paging) so a facet selected on the table narrows both.
 *
 *  `undefined` is in the fetched type, not just in `QueryState.data`, because a
 *  missing endpoint resolves rather than rejects (see the latch above). */
export function useErrorBreakdown(
  params: Omit<ErrorListParams, "limit" | "cursor"> = {},
  override?: AnalyticsQuery,
): QueryState<ErrorBreakdownResponse | undefined> {
  const client = useAnalyticsClient();
  const query = useScopedQuery(override);
  // Resolving `undefined` rather than rethrowing is what keeps the expected 404
  // out of the api-failed funnel: the panel already reads absent data as "not
  // available yet". Every other failure propagates and is tracked as usual.
  const read = async (): Promise<ErrorBreakdownResponse | undefined> => {
    try {
      return await client.getErrorBreakdown({ ...query, ...params });
    } catch (err) {
      if (toApiError(err)?.code !== "not_found") throw err;

      breakdownUnavailable = true;
      logger.warn(
        err instanceof Error ? err : new Error("errors breakdown unavailable"),
        "errors breakdown endpoint not deployed",
        { origin: "loadErrorBreakdown" },
      );
      return undefined;
    }
  };
  return useQuery(
    read,
    queryKey(query, `errors-breakdown-${JSON.stringify(params)}`),
    "loadErrorBreakdown",
    !breakdownUnavailable,
  );
}

export function useSession(
  sessionId: string,
  override?: AnalyticsQuery,
): QueryState<SessionDetailResponse> {
  const client = useAnalyticsClient();
  const query = useScopedQuery(override);
  return useQuery(
    () => client.getSession(sessionId, query),
    queryKey(query, `session-${sessionId}`),
    "loadSessionDetail",
  );
}
